import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import type { LocaleType } from '@/shared/i18n/i18n';
import { translateSecuritySchemeToAuth } from '@tarslib/base/lib/AuthBuilder';
import {
  authConfigsFlowsFormItemName,
  getAddTokenToFromItemName,
  getAuthorizationUrlFormItemName,
  getHeaderPrefixFormItemName,
  getQueryParamKeyFormItemName,
  getRefreshUrlFormItemName,
  getScopesFormItemName,
  getTokenUrlFormItemName,
} from '@tarslib/base/lib/SecurityScheme';
import type { AuthType, SecuritySchemeUseCase } from '@tarslib/types/lib/auth';
import {
  AuthLabelMap,
  AuthValue,
  LegacySecuritySchemeAuthType,
  oAuth2AddTokenToDefaultValue,
  OAuth2FlowsType,
} from '@tarslib/types/lib/auth';
import type { IHttpFolderMap } from '@tarslib/types/lib/httpFolder';
import type { IPostRequestContextVariablesItem } from '@tarslib/types/lib/postRequest';
import type { ISecurityScheme, ISecuritySchemeMap } from '@tarslib/types/lib/securityScheme';
import { Collapse, Divider, Tag } from 'aui';
import { FoxIconRightMd } from 'aui-biz-icons';
import clsx from 'clsx';
import get from 'lodash-es/get';
import { useState } from 'react';
import FormattedHTMLMessage from '../formatted-html-message';
import styles from './Authorization.module.css';

const { Panel } = Collapse;

type AuthorizationProps = {
  id: number;
  auth: AuthType | undefined;
  securityScheme?: SecuritySchemeUseCase;
};

export function getAuthFlowTitle(key: string) {
  switch (key) {
    case OAuth2FlowsType.AUTHORIZATION_CODE:
      return 'Authorization Code';
    case OAuth2FlowsType.IMPLICIT:
      return 'Implicit';
    case OAuth2FlowsType.CLIENT_CREDENTIALS:
      return 'Client Credentials';
    case OAuth2FlowsType.PASSWORD:
      return 'Password';
    default:
      return '';
  }
}

export interface IRenderAuth {
  auth: AuthType;
  securityScheme?: SecuritySchemeUseCase;
  inheritFrom?: 'folder' | 'project';
  folderName?: string;
}

const NO_AUTH = { type: AuthValue.NoAuth };
const collapseKey = 'authorization';

function isEmptyObj(obj: Record<string, any>): boolean {
  if (!obj) return true;
  return Object.keys(obj).length === 0;
}

export function getAuthToRender(
  httpApiAuth?: { auth?: AuthType; securityScheme?: SecuritySchemeUseCase },
  folderAuth?: {
    folderName: string;
    auth?: AuthType;
    securityScheme?: SecuritySchemeUseCase;
  },
  commonAuth?: { auth?: AuthType; securityScheme?: SecuritySchemeUseCase },
): IRenderAuth | undefined {
  const result: any = {};

  if (httpApiAuth && !isEmptyObj(httpApiAuth?.auth || {})) {
    result.auth = httpApiAuth?.auth ?? {};
    result.securityScheme = httpApiAuth?.securityScheme ?? {};
    return result;
  }
  if (folderAuth && folderAuth?.auth) {
    result.auth = folderAuth?.auth ?? {};
    result.securityScheme = folderAuth?.securityScheme ?? {};
    result.inheritFrom = 'folder';
    result.folderName = folderAuth.folderName;
    return result;
  }
  if (commonAuth && !isEmptyObj(commonAuth)) {
    result.auth = commonAuth?.auth ?? {};
    result.securityScheme = commonAuth?.securityScheme ?? {};
    result.inheritFrom = 'project';
    return result;
  }

  return undefined;
}

export const getFolderAuthByFolderId = (
  httpFolderMap: IHttpFolderMap,
  folderId: number,
):
  | {
      folderName: string;
      auth: AuthType | undefined;
      securityScheme: SecuritySchemeUseCase | undefined;
    }
  | undefined => {
  const result = {
    folderName: '',
    auth: undefined,
    securityScheme: undefined,
  };
  if (folderId === 0) {
    return undefined;
  }

  const myFolder = httpFolderMap[folderId];
  if (!myFolder) return result;

  result.folderName = myFolder.name;

  const { auth, securityScheme } = myFolder;
  if (!auth) return result;

  if ((auth.type === AuthValue.InHerit || isEmptyObj(auth)) && folderId !== 0) {
    return getFolderAuthByFolderId(httpFolderMap, myFolder.parentId);
  }

  if (auth.type && auth.type === AuthValue.NoAuth) {
    return {
      folderName: myFolder.name,
      auth: NO_AUTH,
      securityScheme: undefined,
    };
  }
  return {
    folderName: myFolder.name,
    auth,
    securityScheme,
  };
};

interface SecuritySchemeOAuth2Props {
  securityScheme?: ISecurityScheme;
  scopes?: string[];
}

export const replaceVariable = (value: string, variables: IPostRequestContextVariablesItem[]) => {
  let result = value;
  variables.forEach((variable: IPostRequestContextVariablesItem) => {
    result = result ? String(result).replaceAll(`{{${variable.name}}}`, variable.value) : '';
  });
  return result;
};

function SecuritySchemeOAuth2Detail(props: SecuritySchemeOAuth2Props) {
  const { securityScheme, scopes } = props;
  const flows = get(securityScheme, authConfigsFlowsFormItemName) || {};
  const flowsKeys = Object.keys(flows);
  const selectedScopes = scopes || [];
  const [selectedFlow, setSelectedFlow] = useState(flowsKeys[0]);
  const environment = useDocsDataModelStore((x) => x.environments.selectedEnvironment);
  const variables = environment?.variables || [];

  return (
    <div>
      <div className={styles['security-scheme-title']}>
        <div className={styles['auth-type']}>{AuthLabelMap.oauth2}</div>
        <div className={styles['oauth2-flow-tags']}>
          {flowsKeys.map((flowName) => (
            <div
              key={flowName}
              onClick={() => setSelectedFlow(flowName)}
              className={clsx(
                styles['oauth2-flow-tag'],
                selectedFlow === flowName && styles['oauth2-flow-tag-selected'],
              )}
            >
              {getAuthFlowTitle(flowName) || flowName}
            </div>
          ))}
        </div>
      </div>
      {flowsKeys.map((flowName) => {
        if (flowName !== selectedFlow) return null;
        const authUrl = get(securityScheme, getAuthorizationUrlFormItemName(flowName));
        const tokenUrl = get(securityScheme, getTokenUrlFormItemName(flowName));
        const refreshUrl = get(securityScheme, getRefreshUrlFormItemName(flowName));
        const scopesDefinition = get(securityScheme, getScopesFormItemName(flowName)) || {};
        const addTokenTo =
          get(securityScheme, getAddTokenToFromItemName(flowName)) || oAuth2AddTokenToDefaultValue;
        const headerPrefix = get(securityScheme, getHeaderPrefixFormItemName(flowName)) || 'Bearer';
        const queryParamKey =
          get(securityScheme, getQueryParamKeyFormItemName(flowName)) || 'access_token';

        const scopeList = Object.keys(scopesDefinition).reduce<JSX.Element[]>((result, scope) => {
          if (selectedScopes.includes(scope)) {
            result.push(
              <div className={clsx(styles.scopes, 'truncate')} key={scope}>
                <span className={styles['scope-name']}>
                  <code>{scope}</code>
                </span>
                {scopesDefinition[scope] && (
                  <>
                    <span className={styles['scope-divider']}>-</span>
                    <span>{scopesDefinition[scope]}</span>
                  </>
                )}
              </div>,
            );
          }
          return result;
        }, []);

        return (
          <div key={flowName}>
            <AuthDetail
              auth={{
                type: AuthValue.OAuth2,
                oauth2: {
                  addTokenTo,
                  queryParamKey,
                  headerPrefix,
                } as any,
              }}
            />
            {(authUrl || tokenUrl || refreshUrl || scopeList.length > 0) && (
              <>
                <Divider className="my-3" dashed />
                <div className={styles['flow-config-detail']}>
                  {authUrl && <div>Authorize URL: {replaceVariable(authUrl, variables)}</div>}
                  {tokenUrl && <div>Token URL: {replaceVariable(tokenUrl, variables)}</div>}
                  {refreshUrl && <div>Refresh URL: {replaceVariable(refreshUrl, variables)}</div>}
                  {scopeList.length > 0 && (
                    <div className={styles['scopes-group']}>
                      <div>Scopes: </div>
                      <div className="overflow-hidden">{scopeList}</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface ExampleProps {
  example?: string;
  xAmzDate?: string;
}

function Example(props: ExampleProps) {
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  if (!props.example) {
    return null;
  }
  return (
    <div className="flex justify-start">
      <span className="whitespace-no-wrap mr-2">
        {clientI18n('api.label.example')}
        {clientI18n('colon')}
      </span>
      <div>
        <Tag className="block whitespace-normal break-all" outline>
          <code>{props.example}</code>
        </Tag>
        {props.xAmzDate && (
          <Tag className="mt-1" outline>
            {props.xAmzDate}
          </Tag>
        )}
      </div>
    </div>
  );
}

function getAuthorizationHeaderExample(headerPrefix?: string) {
  const prefix = headerPrefix ? headerPrefix + ' ' : '';
  return 'Authorization: ' + prefix + '********************';
}

function getOAuth2AuthDetail(
  props: AuthDetailProps & {
    clientI18n: (key: keyof LocaleType, params?: Record<string, string>) => string;
  },
) {
  const { auth, clientI18n } = props;
  const position = auth?.oauth2?.addTokenTo ?? oAuth2AddTokenToDefaultValue;
  const queryParamKey = auth?.oauth2?.queryParamKey ?? 'access_token';
  const headerPrefix = auth?.oauth2?.headerPrefix ?? 'Bearer';
  if (position === 'header') {
    return (
      <>
        <div className="mb-1">
          <FormattedHTMLMessage
            id="api.label.authorization.jwt.description1"
            values={{ addTokenTo: clientI18n('header'), name: 'Authorization' }}
          />
          {headerPrefix && (
            <FormattedHTMLMessage
              id="api.label.authorization.jwt.description2"
              values={{ headerPrefix }}
            />
          )}
        </div>
        <Example example={getAuthorizationHeaderExample(headerPrefix)} />
      </>
    );
  }
  return (
    <>
      <div className="mb-1">
        <FormattedHTMLMessage
          id="api.label.authorization.jwt.description1"
          values={{ addTokenTo: 'Query', name: queryParamKey }}
        />
      </div>
      <Example example={`?${queryParamKey}=********************`} />
    </>
  );
}

function getJwtAuthDetail(
  props: AuthDetailProps & {
    clientI18n: (key: keyof LocaleType, params?: Record<string, string>) => string;
  },
) {
  const { auth, clientI18n } = props;
  const position = auth?.jwt?.addTokenTo || 'header';
  const queryParamKey = auth?.jwt?.queryParamKey || 'token';
  const headerPrefix = auth?.jwt?.headerPrefix;
  if (position === 'header') {
    return (
      <>
        <div className="mb-1">
          <FormattedHTMLMessage
            id="api.label.authorization.jwt.description1"
            values={{ addTokenTo: clientI18n('header'), name: 'Authorization' }}
          />
          {headerPrefix && (
            <FormattedHTMLMessage
              id="api.label.authorization.jwt.description2"
              values={{ headerPrefix }}
            />
          )}
        </div>
        <Example example={getAuthorizationHeaderExample(headerPrefix)} />
      </>
    );
  }
  return (
    <>
      <div className="mb-1">
        <FormattedHTMLMessage
          id="api.label.authorization.jwt.description1"
          values={{ addTokenTo: 'Query', name: queryParamKey }}
        />
      </div>
      <Example example={`?${queryParamKey}=********************`} />
    </>
  );
}

interface AuthDetailProps {
  auth: AuthType;
  options?: {
    securityScheme?: ISecurityScheme;
    scopes?: string[];
  };
}

function AuthDetail(props: AuthDetailProps) {
  const { auth } = props;
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  switch (auth.type) {
    case AuthValue.OAuth2: {
      if (props.options?.securityScheme) {
        return (
          <SecuritySchemeOAuth2Detail
            securityScheme={props.options?.securityScheme}
            scopes={props.options?.scopes}
          />
        );
      }
      return getOAuth2AuthDetail({ ...props, clientI18n });
    }
    case AuthValue.APIKey: {
      const key = auth?.apikey?.key ? auth.apikey.key : 'X-Token';
      return (
        <>
          <div className="mb-1">
            <FormattedHTMLMessage
              id="api.label.authorization.description.apikey"
              values={{ position: auth?.apikey?.in || 'header', key: auth?.apikey?.key || '' }}
            />
          </div>
          <Example example={`${key}: ********************`} />
        </>
      );
    }
    case AuthValue.BearerToken: {
      return (
        <>
          <div className="mb-1">
            <FormattedHTMLMessage id="api.label.authorization.description.bearer" />
          </div>
          <Example example="Authorization: Bearer ********************" />
        </>
      );
    }
    case AuthValue.BasicAuth: {
      return (
        <>
          <div className="mb-1">
            {' '}
            <FormattedHTMLMessage id="api.label.authorization.description.basic" />
          </div>
          <Example example="Authorization: Basic *****************" />
        </>
      );
    }
    case AuthValue.DigestAuth: {
      return (
        <>
          <div className="mb-1">
            <FormattedHTMLMessage id="api.label.authorization.description.digest" />
          </div>
          <Example example='Authorization: Digest username=guest, realm="test", nonce="2", uri="/uri", response="***"' />
        </>
      );
    }
    case AuthValue.OAuth1: {
      let position = auth?.oauth1?.in || 'header';
      if (position === 'body') {
        position = 'body | url';
      }
      const example =
        position === 'header'
          ? 'Authorization: ******* access_token=********************'
          : 'oauth_token=********************&oauth_signature_method=*******';
      return <Example example={example} />;
    }
    case AuthValue.Hawk: {
      return (
        <>
          <div className="mb-1">
            <FormattedHTMLMessage id="api.label.authorization.description.hawk" />
          </div>
          <Example example='Authorization: Hawk id="***", ts="******", nonce="******", mac="******"' />
        </>
      );
    }
    case AuthValue.AWS: {
      const position = auth?.awsv4?.in || 'header';
      const example =
        position === 'header'
          ? 'Authorization: ****** Credential=******, SignedHeaders=host;x-amz-date, Signature=******'
          : 'X-Amz-Date=******&X-Amz-Algorithm=******&X-Amz-Credential=********************&X-Amz-SignedHeaders=host&X-Amz-Signature=********************';
      const xAmzDate = position === 'header' ? 'X-Amz-Date: ******' : undefined;
      const descriptionId =
        position === 'header'
          ? 'api.label.authorization.description.aws'
          : 'api.label.authorization.description.aws.url';
      return (
        <>
          <div className="mb-1">
            <FormattedHTMLMessage id={descriptionId} />
          </div>
          <Example example={example} xAmzDate={xAmzDate} />
        </>
      );
    }
    case AuthValue.NTLM: {
      return <Example example="Authorization: username=guest, password=***" />;
    }
    case AuthValue.Kerberos: {
      return <Example example="Authorization: Negotiate ********************" />;
    }
    case AuthValue.EdgeGrid: {
      return (
        <>
          <div className="mb-1">
            <FormattedHTMLMessage id="api.label.authorization.description.edgegrid" />
          </div>
          <Example example="Authorization: EG1-HMAC-SHA256 client_token=********************;access_token=********************;timestamp=******;nonce=********************;signature=********************" />
        </>
      );
    }
    case AuthValue.JWT: {
      return getJwtAuthDetail({ ...props, clientI18n });
    }
    default: {
      return null;
    }
  }
}

interface SecuritySchemeDetailProps {
  securitySchemeUseCase: SecuritySchemeUseCase;
  securitySchemeMap: ISecuritySchemeMap;
  scopes?: Record<string, Record<number, string[]>>;
}

function SecuritySchemeDetail(props: SecuritySchemeDetailProps) {
  const { securitySchemeUseCase, securitySchemeMap, scopes } = props;
  let schemeGroups = securitySchemeUseCase.schemeGroups;
  if (!Array.isArray(schemeGroups)) {
    schemeGroups = [];
  }
  return (
    <>
      {schemeGroups.map((schemeGroup) => {
        const schemeId = schemeGroup.schemeIds[0];
        if (!schemeId) {
          return null;
        }
        const securityScheme = securitySchemeMap[schemeId];
        if (!securityScheme) {
          return null;
        }
        const auth = translateSecuritySchemeToAuth(securityScheme);
        if (!auth) {
          return null;
        }
        const selectedScopes = scopes?.[schemeGroup.id]?.[schemeId] || [];
        return (
          <div key={schemeGroup.id} className={styles['auth-detail']}>
            {auth.type !== AuthValue.OAuth2 && (
              <div className={styles['security-scheme-title']}>
                <div className={styles['auth-type']}>
                  {AuthLabelMap[auth.type as keyof typeof AuthLabelMap]}
                </div>
              </div>
            )}
            <AuthDetail auth={auth} options={{ securityScheme, scopes: selectedScopes }} />
          </div>
        );
      })}
    </>
  );
}

interface InheritInfoProps {
  definitionInheritFrom?: { name: string; id: number };
}

function InheritInfo(props: InheritInfoProps) {
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  if (!props.definitionInheritFrom) {
    return null;
  }
  if (props.definitionInheritFrom.id === 0) {
    return clientI18n('api.label.authorization.inheritLabel.project');
  }
  return (
    <FormattedHTMLMessage
      id="api.label.authorization.inheritLabel.folder"
      values={{ folderName: props.definitionInheritFrom.name }}
    />
  );
}

const AuthorizationCollapse = ({
  definitionInheritFrom,
  auth,
  securityScheme,
  scopes,
}: {
  definitionInheritFrom?: { name: string; id: number };
  auth: NonNullable<ReturnType<typeof getAuthToRender>>['auth'];
  securityScheme: NonNullable<ReturnType<typeof getAuthToRender>>['securityScheme'];
  scopes?: Record<string, Record<number, string[]>>;
}) => {
  const securitySchemeMap = useDocsDataModelStore(
    (x) => (x.resourceData.extraData as ResourceHttpApiData['extraData']).securitySchemeMap,
  );

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const isSecurityScheme = [AuthValue.SecurityScheme, LegacySecuritySchemeAuthType].includes(
    auth.type || '',
  );

  const titleContent = (
    <div className="text-paragraph font-semibold leading-6">
      {clientI18n('api.label.authorization')}
    </div>
  );

  return (
    <Collapse
      expandIcon={() => <FoxIconRightMd />}
      className={clsx('overflow-hidden', styles.authorization)}
      defaultActiveKey="authorization"
    >
      <Panel header={titleContent} key={collapseKey}>
        <div className={styles['panel-content']}>
          {!isSecurityScheme && <AuthDetail auth={auth} />}
          {isSecurityScheme && (
            <SecuritySchemeDetail
              securitySchemeUseCase={securityScheme || {}}
              securitySchemeMap={securitySchemeMap || {}}
              scopes={scopes}
            />
          )}
        </div>
      </Panel>
    </Collapse>
  );
};

export function isAuthInherit(obj?: Record<string, any>) {
  if (!obj) return true;
  if (obj && obj.type === AuthValue.InHerit) return true;
  return Object.keys(obj).length === 0;
}

export function Authorization(props: AuthorizationProps) {
  const inheritAuthInfo = useDocsDataModelStore(
    (x) => (x.resourceData.extraData as ResourceHttpApiData['extraData']).inheritAuthInfo,
  );

  const isInherit = isAuthInherit(props?.auth);
  const auth = isInherit ? inheritAuthInfo?.auth : props?.auth;
  const securityScheme = isInherit ? inheritAuthInfo?.securityScheme : props?.securityScheme;

  if (!auth?.type) {
    return null;
  }

  if (auth.type === AuthValue.NoAuth) return null;

  return (
    <AuthorizationCollapse
      definitionInheritFrom={isInherit ? inheritAuthInfo?.definitionInheritFrom : undefined}
      auth={auth}
      securityScheme={securityScheme}
      scopes={{
        ...inheritAuthInfo?.securityScheme?.scopes,
        ...props?.securityScheme?.scopes,
      }}
    />
  );
}
